import { type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { usePaginated } from '@/hooks/use-paginated'
import type { Giveaway } from '@/models/giveaway'
import { GiveawayCard, GiveawayCardSkeleton } from './GiveawayCard'

interface GiveawayFeedProps {
  giveaways: Giveaway[]
  isPending: boolean
  /** A background refetch dims the grid instead of emptying it. */
  isRefetching?: boolean
  onSelect: (giveaway: Giveaway) => void
  /** Rendered over each card — the edit affordance on "My Giveaways". */
  renderCardAction?: (giveaway: Giveaway) => ReactNode
  emptyTitle?: string
  emptyBody?: string
  skeletonCount?: number
  className?: string
  /**
   * Server paging. When the caller fetches page by page it passes these, and
   * the whole list it hands down is already what should be on screen. Left out
   * — as on "My Giveaways", which gets one complete list — the grid pages what
   * it was given.
   */
  hasMore?: boolean
  loadMore?: () => void
  isLoadingMore?: boolean
}

/**
 * The paged card grid, shared by the home feed, search, a creator's page and
 * "My Giveaways" — four hand-copied loops before this existed.
 */
export function GiveawayFeed({
  giveaways,
  isPending,
  isRefetching = false,
  onSelect,
  renderCardAction,
  emptyTitle = 'Nothing here yet',
  emptyBody = 'No giveaways are running right now.',
  skeletonCount = 6,
  className,
  hasMore: serverHasMore,
  loadMore: serverLoadMore,
  isLoadingMore = false,
}: GiveawayFeedProps) {
  const paged = usePaginated(giveaways)

  // Server paging when the caller offers it, client paging otherwise. The
  // count line only claims a total when one is actually known: with server
  // paging there is no total until the last page arrives, and "Showing 12 of
  // 12" beside a "Load more" button is a contradiction.
  const server = serverLoadMore !== undefined
  const visible = server ? giveaways : paged.visible
  const hasMore = server ? (serverHasMore ?? false) : paged.hasMore
  const loadMore = server ? serverLoadMore : paged.loadMore

  if (isPending) {
    return (
      <div className={cn('giveaways', className)}>
        {Array.from({ length: skeletonCount }, (_, index) => (
          <GiveawayCardSkeleton key={index} />
        ))}
      </div>
    )
  }

  if (giveaways.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">{emptyTitle}</div>
        <div className="empty-state-body">{emptyBody}</div>
      </div>
    )
  }

  return (
    <>
      <div className={cn('giveaways', isRefetching && 'is-refetching', className)}>
        {visible.map((giveaway, index) =>
          renderCardAction ? (
            <div className="giveaway-card-wrap" key={giveaway.id}>
              <GiveawayCard giveaway={giveaway} index={index} onSelect={onSelect} />
              {renderCardAction(giveaway)}
            </div>
          ) : (
            <GiveawayCard key={giveaway.id} giveaway={giveaway} index={index} onSelect={onSelect} />
          ),
        )}
      </div>

      {hasMore ? (
        <div className="feed-pagination">
          <button type="button" className="cta" onClick={loadMore} disabled={isLoadingMore}>
            {isLoadingMore ? <span className="spinner-inline" aria-hidden="true" /> : null}
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
          <div className="feed-pagination-count" aria-live="polite">
            {server
              ? `Showing ${visible.length}`
              : `Showing ${paged.shownCount} of ${paged.totalCount}`}
          </div>
        </div>
      ) : null}
    </>
  )
}
